// Service de synchronisation des instances de modules

import { ModulesApiService } from './ModulesApiService';
import { ModuleInstancesApiService } from './ModuleInstancesApiService';
import type { InstanceSyncResult, InstanceParameter, InstanceData } from './types';
import type {Config, LayoutItem} from '../Config/config';
import { getComponentMetadata } from '../Core/ComponentRegistry';
import { isConfigLocalStorageEnabled } from '../Config/devMode';

export class InstanceSyncService {
    private modulesApiService: ModulesApiService;
    private instancesApiService: ModuleInstancesApiService;

    constructor(modulesApiService: ModulesApiService, instancesApiService: ModuleInstancesApiService) {
        this.modulesApiService = modulesApiService;
        this.instancesApiService = instancesApiService;
    }

    /**
     * Synchronise les instances locales avec l'API
     */
    async syncInstances(
        localItems: LayoutItem[],
        pageUrl?: string,
        options: { dryRun?: boolean; concurrency?: number } = {}
      ): Promise<InstanceSyncResult> {
        const result: InstanceSyncResult = {
          created: [],
          updated: [],
          deleted: [],
          errors: []
        };
      
        const dryRun = !!options.dryRun;
        const concurrency = options.concurrency && options.concurrency > 0 ? options.concurrency : 6;
      
        // ---- Helpers ----
        function extractError(e: any): string {
          try {
            if (!e) return "Unknown error";
            if (e.response && e.response.data) return typeof e.response.data === "string" ? e.response.data : JSON.stringify(e.response.data);
            if (e.message) return e.message;
            return String(e);
          } catch {
            return "Error extracting error message";
          }
        }
      
        // deterministic deep stringify
        function stableStringify(obj: any): string {
          const seen = new WeakSet();
          function sorter(a: any, b: any) {
            if (a < b) return -1;
            if (a > b) return 1;
            return 0;
          }
          function _stable(o: any): any {
            if (o === null || typeof o !== "object") return o;
            if (seen.has(o)) return "[Circular]";
            seen.add(o);
            if (Array.isArray(o)) return o.map(_stable);
            const keys = Object.keys(o).sort(sorter);
            const res: any = {};
            for (const k of keys) res[k] = _stable(o[k]);
            return res;
          }
          try {
            return JSON.stringify(_stable(obj));
          } catch {
            return JSON.stringify(obj);
          }
        }
      
        function slugify(s: string): string {
          if (!s) return "";
          return String(s)
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-zA-Z0-9._\-:\/]/g, "-")
            .replace(/-+/g, "-")
            .replace(/^\-+|\-+$/g, "");
        }
      
        async function mapWithConcurrency<T, R>(items: T[], worker: (t: T) => Promise<R>, maxConcurrency = 6) {
          const results: R[] = new Array(items.length);
          let idx = 0;
          const runners = new Array(Math.min(maxConcurrency, items.length)).fill(null).map(async () => {
            while (true) {
              const i = idx++;
              if (i >= items.length) break;
              try {
                results[i] = await worker(items[i]);
              } catch (e) {
                results[i] = undefined as any;
              }
            }
          });
          await Promise.all(runners);
          return results;
        }
      
        const makeKey = (page: string, instanceName: string) => `${page}::${instanceName}`;
      
        // Minimal runtime type guard for InstanceData (adapt to real shape)
        function isInstanceData(v: any): v is InstanceData {
          // Exemple minimal : InstanceData doit être un objet (non-null). Ajuste selon ta définition.
          return v !== null && typeof v === "object";
        }
      
        try {
          // 1) modules
          let apiModules: any[] = [];
          try {
            apiModules = await this.modulesApiService.getAllModules();
          } catch (err) {
            result.errors.push(`Erreur getAllModules: ${extractError(err)}`);
            return result;
          }
      
          const localModuleNames = this.getLocalModuleNames ? this.getLocalModuleNames() : [];
          const localModuleSet = new Set(localModuleNames.map((n: string) => String(n).toLowerCase()));
          const allowedModules = Array.isArray(apiModules)
            ? apiModules.filter(m => m && m.name && localModuleSet.has(String(m.name).toLowerCase()))
            : [];
          const modulesByName = new Map<string, any>();
          for (const m of allowedModules) modulesByName.set(String(m.name).toLowerCase(), m);
          console.log(`✅ Modules autorisés pour sync: ${allowedModules.length}/${(apiModules || []).length}`);
      
          // 2) récupérer instances par page
          let pagesToFetch: string[] = [];
          if (pageUrl) pagesToFetch = [pageUrl];
          else {
            try {
              const allPages = this.getAllPagesFromCurrentConfig ? this.getAllPagesFromCurrentConfig() : [];
              pagesToFetch = Array.isArray(allPages) ? allPages : [];
            } catch (err) {
              result.errors.push(`Erreur getAllPagesFromCurrentConfig: ${extractError(err)}`);
              pagesToFetch = [];
            }
          }
          pagesToFetch = pagesToFetch.map(p => (p ? String(p) : "/"));
      
          const apiInstances: any[] = [];
          for (const p of pagesToFetch) {
            try {
              const pageInstances = await this.instancesApiService.getAllInstances(p);
              if (Array.isArray(pageInstances)) apiInstances.push(...pageInstances);
              else console.log(`⚠️ getAllInstances(${p}) n'a pas retourné un tableau`);
            } catch (err) {
              result.errors.push(`Erreur getAllInstances(${p}): ${extractError(err)}`);
            }
          }
          console.log(`📥 Total instances récupérées: ${apiInstances.length}`);
      
          // 3) map instances
          const instancesMap = new Map<string, any>();
          for (const inst of apiInstances) {
            const instPage = inst && inst.page ? String(inst.page) : "/";
            const instName = inst && inst.instance_name ? String(inst.instance_name) : "";
            const key = makeKey(instPage, instName);
            if (!instancesMap.has(key)) instancesMap.set(key, inst);
          }
      
          // 4) local items pertinents
          const relevantLocalItems = pageUrl
            ? localItems.filter(item => String(item.page || "/") === pageUrl)
            : localItems.slice();
      
          // 5) traiter items
          await mapWithConcurrency(
            relevantLocalItems,
            async (item) => {
              try {
                const comp = item.component ? String(item.component) : "";
                if (!localModuleSet.has(comp.toLowerCase())) {
                  console.log(`⚠️ Module ${comp} ignoré (non défini localement)`);
                  return;
                }
      
                const itemPage = pageUrl ? pageUrl : String(item.page || "/");
                if (pageUrl && String(item.page || "/") !== pageUrl) {
                  console.log(`⏭️ Item ${item.id} ignoré (page ${item.page} != ${pageUrl})`);
                  return;
                }
      
                const apiModule = modulesByName.get(comp.toLowerCase());
                if (!apiModule) {
                  result.errors.push(`Module ${comp} non trouvé dans l'API`);
                  return;
                }
      
                const instanceName = slugify(String(item.id));
                const key = makeKey(itemPage, instanceName);
      
                // === IMPORTANT ===
                // convertPropsToInstanceData might return Record<string, unknown> | InstanceData.
                // We ensure here that we pass an InstanceData to the API by casting or validating.
                let instanceDataRaw: any = this.convertPropsToInstanceData
                  ? this.convertPropsToInstanceData(item.component, item.props || {})
                  : (item.props || {});
      
                // Optionnel : validate and coerce
                if (!isInstanceData(instanceDataRaw)) {
                  // si invalid, on transforme (ou on enregistre une erreur). Ici on force la conversion en objet vide pour éviter crash.
                  result.errors.push(`Warning: instanceData for ${instanceName} didn't match InstanceData shape — coerced to object.`);
                  instanceDataRaw = {};
                }
      
                const instanceData: InstanceData = instanceDataRaw as InstanceData;
      
                const existingInstance = instancesMap.get(key);
      
                if (!existingInstance) {
                  const targetPage = itemPage || "/";
                  console.log(`📄 Création instance ${instanceName} pour page: ${targetPage}`);
      
                  const createDto: any = {
                    module_id: apiModule.id,
                    instance_name: instanceName,
                    description: `Instance de ${item.component}`,
                    // <-- ici on force le type
                    data: instanceData as InstanceData,
                    page: targetPage,
                    position: typeof item.positions === 'number' ? item.positions : 0
                  };
      
                  try {
                    const createdInstance = await this.instancesApiService.createInstance(createDto);
                    instancesMap.set(key, createdInstance);
                    if (createdInstance && createdInstance.is_published === false) {
                      try {
                        await this.instancesApiService.updateInstance(createdInstance.id, { is_published: true });
                      } catch (errPub) {
                        result.errors.push(`Erreur publication ${instanceName}: ${extractError(errPub)}`);
                      }
                    }
                    result.created.push(instanceName);
                  } catch (errCreate) {
                    result.errors.push(`Erreur création ${instanceName}: ${extractError(errCreate)}`);
                  }
                } else {
                  const oldData = existingInstance.data || {};
                  const oldStr = stableStringify(oldData);
                  const newStr = stableStringify(instanceData);
                  if (oldStr !== newStr) {
                    const targetPage = itemPage || "/";
                    console.log(`📄 Mise à jour instance ${instanceName} (id=${existingInstance.id}) pour page: ${targetPage}`);
                    try {
                      await this.instancesApiService.updateInstance(existingInstance.id, {
                        instance_name: instanceName,
                        description: existingInstance.description || `Instance de ${item.component}`,
                        // <-- cast pour satisfaire TS
                        data: instanceData as InstanceData,
                        page: targetPage,
                        is_published: true
                      });
                      instancesMap.set(key, { ...existingInstance, data: instanceData, page: targetPage, instance_name: instanceName, is_published: true });
                      result.updated.push(instanceName);
                    } catch (errUpd) {
                      result.errors.push(`Erreur mise à jour ${instanceName}: ${extractError(errUpd)}`);
                    }
                  }
                }
              } catch (errWorker) {
                result.errors.push(`Erreur traitement item ${item && item.id ? item.id : "<unknown>"}: ${extractError(errWorker)}`);
              }
            },
            concurrency
          );
      
          // 6) suppression
          const localInstanceKeys = new Set<string>();
          for (const li of relevantLocalItems) {
            const lp = String(li.page || "/");
            const iname = slugify(String(li.id));
            localInstanceKeys.add(makeKey(lp, iname));
          }
      
          for (const inst of apiInstances) {
            const instPage = inst && inst.page ? String(inst.page) : "/";
            const instName = inst && inst.instance_name ? String(inst.instance_name) : "";
            const key = makeKey(instPage, instName);
            if (!localInstanceKeys.has(key)) {
              if (dryRun) {
                result.deleted.push(instName);
                console.log(`🗑️ [dryRun] Suppression prévue: ${instName} (page ${instPage})`);
              } else {
                try {
                  await this.instancesApiService.deleteInstance(inst.id);
                  result.deleted.push(instName);
                  console.log(`🗑️ Suppression: ${instName} (page ${instPage})`);
                } catch (errDel) {
                  result.errors.push(`Erreur suppression ${instName}: ${extractError(errDel)}`);
                }
              }
            }
          }
        } catch (err) {
          result.errors.push(`Erreur générale: ${err instanceof Error ? err.message : String(err)}`);
        }
      
        return result;
      }
      
      

    /**
     * Compare les instances locales avec l'API sans les modifier
     */
    async compareInstances(localItems: LayoutItem[], pageUrl?: string): Promise<InstanceSyncResult> {
        const result: InstanceSyncResult = {
            created: [],
            updated: [],
            deleted: [],
            errors: []
        };

        try {
            const apiModules = await this.modulesApiService.getAllModules();
            
            // 🆕 Filtrer les modules pour ne garder que ceux définis dans la config locale
            const localModuleNames = this.getLocalModuleNames();
            const allowedModules = apiModules.filter(module => localModuleNames.includes(module.name));
            console.log(`✅ Modules autorisés pour comparaison: ${allowedModules.length}/${apiModules.length}`);
            
            const modulesByName = new Map(allowedModules.map(m => [m.name, m]));

            // Récupérer les instances existantes
            let apiInstances;
            if (pageUrl) {
                // Page spécifique
                apiInstances = await this.instancesApiService.getAllInstances(pageUrl);
                console.log(`📥 Instances récupérées pour comparaison page ${pageUrl}: ${apiInstances.length}`);
            } else {
                // Toutes les pages - récupérer depuis la configuration actuelle
                const allPages = this.getAllPagesFromCurrentConfig();
                console.log(`📄 Comparaison de toutes les pages: ${allPages.join(', ')}`);
                
                // Récupérer les instances de toutes les pages
                apiInstances = [];
                for (const page of allPages) {
                    const pageInstances = await this.instancesApiService.getAllInstances(page);
                    apiInstances.push(...pageInstances);
                }
                console.log(`📥 Total instances récupérées pour comparaison: ${apiInstances.length}`);
            }
            
            const instancesMap = new Map(apiInstances.map(i => [i.instance_name, i]));

            // Instances à créer
            for (const item of localItems) {
                // 🆕 Vérifier que le module est autorisé (défini localement)
                if (!localModuleNames.includes(item.component)) {
                    console.log(`⚠️ Module ${item.component} ignoré dans comparaison (non défini localement)`);
                    continue;
                }
                
                // 🆕 Si pageUrl est spécifié, ne comparer que les items de cette page
                if (pageUrl && item.page !== pageUrl) {
                    console.log(`⏭️ Item ${item.id} ignoré dans comparaison (page ${item.page} != ${pageUrl})`);
                    continue;
                }
                
                const apiModule = modulesByName.get(item.component);
                if (!apiModule) continue;

                if (!instancesMap.has(item.id)) {
                    result.created.push(item.id);
                }
            }

            // Instances à mettre à jour
            for (const item of localItems) {
                // 🆕 Si pageUrl est spécifié, ne comparer que les items de cette page
                if (pageUrl && item.page !== pageUrl) {
                    continue;
                }
                
                const existingInstance = instancesMap.get(item.id);
                if (existingInstance) {
                    const newInstanceData = this.convertPropsToInstanceData(item.component, item.props || {});

                    if (JSON.stringify(existingInstance.data) !== JSON.stringify(newInstanceData)) {
                        result.updated.push(item.id);
                    }
                }
            }

            // Instances à supprimer
            // 🆕 Filtrer les items locaux selon la page si spécifiée
            const relevantLocalItems = pageUrl 
                ? localItems.filter(item => item.page === pageUrl)
                : localItems;
            const localInstanceNames = new Set(relevantLocalItems.map(i => i.id));
            
            for (const instance of apiInstances) {
                if (!localInstanceNames.has(instance.instance_name)) {
                    result.deleted.push(instance.instance_name);
                }
            }

        } catch (error) {
            result.errors.push(`Erreur: ${error instanceof Error ? error.message : String(error)}`);
        }

        return result;
    }

    /**
     * Charge les instances depuis l'API et les fusionne avec la config locale
     * Ne charge que les modules qui sont définis dans la configuration locale
     */
    async loadInstancesFromApi(pageUrl?: string, config?: any): Promise<LayoutItem[]> {
        try {
            console.log('🔄 InstanceSyncService: loadInstancesFromApi, pageUrl =', pageUrl);
            
            // 🆕 Si pageUrl n'est pas spécifié, charger TOUTES les instances de toutes les pages
            // Sinon, charger seulement les instances de la page spécifiée
            let apiInstances;
            if (pageUrl) {
                // Page spécifique
                apiInstances = await this.instancesApiService.getAllInstances(pageUrl);
                console.log(`📥 API Instances reçues pour page ${pageUrl}: ${apiInstances.length}`);
            } else {
                // Toutes les pages - récupérer depuis la configuration actuelle
                const allPages = this.getAllPagesFromCurrentConfig();
                console.log(`📄 Chargement de toutes les pages: ${allPages.join(', ')}`);
                
                // Récupérer les instances de toutes les pages
                apiInstances = [];
                for (const page of allPages) {
                    const pageInstances = await this.instancesApiService.getAllInstances(page);
                    apiInstances.push(...pageInstances);
                }
                console.log(`📥 Total API Instances reçues de toutes les pages: ${apiInstances.length}`);
            }
            
            console.log('📥 Détail instances:', apiInstances);
            
            const apiModules = await this.modulesApiService.getAllModules();
            console.log(`📥 API Modules reçus: ${apiModules.length}`);
            console.log('📥 Détail modules:', apiModules);
            
            // 🆕 Filtrer les modules pour ne garder que ceux définis dans la config locale
            const localModuleNames = this.getLocalModuleNames();
            console.log('🏠 Modules locaux autorisés:', localModuleNames);
            
            const allowedModules = apiModules.filter(module => localModuleNames.includes(module.name));
            console.log(`✅ Modules autorisés: ${allowedModules.length}/${apiModules.length}`);
            console.log('✅ Détail modules autorisés:', allowedModules.map(m => m.name));
            
            const modulesById = new Map(allowedModules.map(m => [m.id, m]));
            console.log('🗂️ Map modules par ID (filtrés):', Array.from(modulesById.keys()));

            const items: LayoutItem[] = [];

            for (const instance of apiInstances) {
                console.log(`\n🔍 Traitement instance: ${instance.instance_name}`);
                console.log(`   - module_id: ${instance.module_id}`);
                console.log(`   - is_active: ${instance.is_active}`);
                console.log(`   - is_published: ${instance.is_published}`);
                
                // ✅ IMPORTANT: le nom du module ne doit PAS être déduit de instance_name (souvent un slug avec des '-')
                // L'API fournit déjà `module_name` (ex: "VideoModule", "Glass_NavBar", "ImageModule")
                const localModuleSet = new Set(localModuleNames.map(n => String(n).toLowerCase()));
                const moduleNameFromApi =
                    (instance && instance.module_name ? String(instance.module_name) : '');
                
                // Fallbacks (si une API legacy ne renvoie pas module_name)
                let resolvedModuleName = moduleNameFromApi;
                if (!resolvedModuleName) {
                    const desc = instance && instance.description ? String(instance.description) : '';
                    const m = desc.match(/Instance de\s+([A-Za-z0-9_]+)/);
                    if (m && m[1]) resolvedModuleName = m[1];
                }
                if (!resolvedModuleName) {
                    // Ultime fallback: uniquement si le préfixe correspond EXACTEMENT à un module local
                    const candidate = instance && instance.instance_name ? String(instance.instance_name).split('-')[0] : '';
                    if (candidate && localModuleSet.has(candidate.toLowerCase())) {
                        resolvedModuleName = candidate;
                    }
                }
                
                console.log(`   - module_name API: ${moduleNameFromApi || '<absent>'}`);
                console.log(`   - module_name résolu: ${resolvedModuleName || '<inconnu>'}`);
                console.log(`   - modules locaux autorisés:`, localModuleNames);
                console.log(`   - module autorisé ?`, resolvedModuleName ? localModuleSet.has(resolvedModuleName.toLowerCase()) : false);
                
                // 🆕 Vérifier que le module est autorisé (défini localement)
                if (!resolvedModuleName || !localModuleSet.has(resolvedModuleName.toLowerCase())) {
                    console.warn(`   ❌ Module ${resolvedModuleName || '<inconnu>'} non autorisé (non défini localement) - Instance ignorée`);
                    continue;
                }
                
                // 🆕 Chercher l'instance existante dans la configuration locale
                const localInstance = config ? this.findLocalInstanceById(instance.instance_name, config) : null;
                console.log(`   - instance locale trouvée:`, localInstance);
                
                if (!localInstance) {
                    console.log(`   🔄 Instance locale ${instance.instance_name} non trouvée - Création d'une nouvelle instance`);
                    // Créer une nouvelle instance avec les données API
                    // Convertir InstanceData (avec parameters) en props simples
                    // Préparer les props en privilégiant instance.props si fourni par l'API, sinon convertir InstanceData.parameters
                    const props: Record<string, any> = instance.props ? { ...instance.props } : {};
                    if (!instance.props && instance.data && instance.data.parameters) {
                        instance.data.parameters.forEach((param: any) => {
                            props[param.key] = param.value;
                        });
                    }
                    const newInstance: LayoutItem = {
                        id: instance.instance_name,
                        component: resolvedModuleName,
                        enabled: true,
                        outsideGrid: false,
                        props: props,
                        positions: {},
                        page: instance.page || '/'
                    };
                    console.log(`   ✅ Nouvelle instance créée:`, newInstance);
                    items.push(newInstance);
                    continue;
                }
                
                if (!instance.is_active) {
                    console.warn(`   ❌ Instance non active`);
                    continue;
                }
                
                if (!instance.is_published) {
                    console.warn(`   ❌ Instance non publiée`);
                    continue;
                }

                // 🆕 Injecter les données API dans l'instance locale existante
                // Convertir InstanceData (avec parameters) en props simples
                const apiProps: Record<string, any> = {};
                if (instance.data && instance.data.parameters) {
                    instance.data.parameters.forEach(param => {
                        apiProps[param.key] = param.value;
                    });
                }
                const updatedInstance: LayoutItem = {
                    ...localInstance,
                    props: {
                        ...localInstance.props,
                        ...apiProps
                    }
                };

                console.log(`   ✅ Instance mise à jour avec données API:`, updatedInstance);
                console.log(`   📦 Données API injectées:`, apiProps);
                items.push(updatedInstance);
            }

            console.log(`\n📦 Total items créés: ${items.length}`);
            console.log('📦 Items:', items);
            
            // 🆕 Si pageUrl est spécifié, filtrer pour ne retourner que les instances de cette page
            if (pageUrl) {
                const filteredItems = items.filter(item => item.page === pageUrl);
                console.log(`🔍 Filtrage pour page ${pageUrl}: ${filteredItems.length}/${items.length} items`);
                return filteredItems;
            }
            
            return items;
        } catch (error) {
            console.error('❌ Erreur lors du chargement des instances:', error);
            return [];
        }
    }

    /**
     * Convertit les props d'un composant en données structurées pour l'API
     */
    private convertPropsToInstanceData(componentName: string, props: Record<string, any>): InstanceData {
        const metadata = getComponentMetadata(componentName);
        const parameters: InstanceParameter[] = [];

        if (metadata && metadata.paramDefinitions) {
            // Pour chaque paramètre défini dans les métadonnées
            for (const [key, definition] of Object.entries(metadata.paramDefinitions)) {
                const value = props[key] !== undefined ? props[key] : metadata.defaultParams[key];
                
                parameters.push({
                    key,
                    display_name: definition.label,
                    value,
                    type: this.mapParamType(definition.type),
                    file_types: definition.file_types || undefined // Ajouter les file_types si disponibles
                });
            }
        } else {
            // Si pas de métadonnées, convertir tous les props (sauf positions)
            for (const [key, value] of Object.entries(props)) {
                if (key === 'positions' || key === 'enabled') continue; // Exclure positions et enabled
                
                parameters.push({
                    key,
                    display_name: this.formatDisplayName(key),
                    value,
                    type: this.inferType(value)
                });
            }
        }

        // Ajouter les propriétés de permissions par paramètre
        const instanceData: InstanceData = { 
            parameters: parameters.map(param => ({
                ...param,
                // Ajouter les permissions du paramètre
                editable_by_dev: this.getParamPermission(metadata, param.key, 'editable_by_dev'),
                editable_by_user: this.getParamPermission(metadata, param.key, 'editable_by_user')
            }))
        };

        return instanceData;
    }

    /**
     * Trouve une instance locale par son ID
     */
    private findLocalInstanceById(instanceId: string, config: Config): LayoutItem | null {
        try {
            // Chercher dans la grille globale
            const globalItem = config.gridLayout.items.find(item => item.id === instanceId);
            if (globalItem) {
                return globalItem;
            }
            
            // Chercher dans les grilles des pages
            for (const page of config.PagesList) {
                if (page.gridLayout && page.gridLayout.items) {
                    const pageItem = page.gridLayout.items.find(item => item.id === instanceId);
                    if (pageItem) {
                        return pageItem;
                    }
                }
            }
            
            return null;
        } catch (error) {
            console.warn('⚠️ Erreur lors de la recherche de l\'instance locale:', error);
            return null;
        }
    }

    /**
     * Récupère les noms des modules définis dans la configuration locale
     */
    private getLocalModuleNames(): string[] {
        // 🆕 Retourner directement les modules connus
        const modules = ['Glass_NavBar', 'ImageModule', 'VideoModule'];
        console.log('🏠 Modules autorisés (hardcodés):', modules);
        return modules;
    }

    /**
     * Récupère toutes les pages (y compris celles ajoutées par l'éditeur) depuis la configuration actuelle
     */
    private getAllPagesFromCurrentConfig(): string[] {
        try {
            const savedConfig = isConfigLocalStorageEnabled() ? localStorage.getItem('gandc_config_backup') : null;
            let currentConfig: any;
            
            if (savedConfig) {
                currentConfig = JSON.parse(savedConfig);
                console.log('📁 Configuration actuelle trouvée dans localStorage pour pages');
            } else {
                // @ts-ignore - import dynamique de config
                currentConfig = require('../Config/config').default;
                console.log('📄 Utilisation de la configuration par défaut pour pages');
            }
            
            const allPages: string[] = [];
            
            if (currentConfig.PagesList && Array.isArray(currentConfig.PagesList)) {
                currentConfig.PagesList.forEach((page: any) => {
                    if (page.pageUrl) {
                        allPages.push(page.pageUrl);
                    }
                });
            }
            
            if (currentConfig.gridLayout && currentConfig.gridLayout.items) {
                currentConfig.gridLayout.items.forEach((item: any) => {
                    if (item.page && !allPages.includes(item.page)) {
                        allPages.push(item.page);
                    }
                });
            }
            
            if (currentConfig.PagesList && Array.isArray(currentConfig.PagesList)) {
                currentConfig.PagesList.forEach((page: any) => {
                    if (page.gridLayout && page.gridLayout.items) {
                        page.gridLayout.items.forEach((item: any) => {
                            if (item.page && !allPages.includes(item.page)) {
                                allPages.push(item.page);
                            }
                        });
                    }
                });
            }
            
            console.log('📄 Toutes les pages trouvées:', allPages);
            return allPages.length > 0 ? allPages : ['/']; // Fallback vers la page d'accueil si aucune page trouvée
        } catch (error) {
            console.warn('⚠️ Erreur lors de la récupération des pages:', error);
            return ['/']; // Fallback vers la page d'accueil
        }
    }

    /**
     * Récupère la permission d'un paramètre depuis les métadonnées
     */
    private getParamPermission(metadata: any, paramKey: string, permission: 'editable_by_dev' | 'editable_by_user'): boolean {
        if (!metadata?.paramDefinitions?.[paramKey]) {
            return permission === 'editable_by_dev' ? true : false; // Par défaut, dev peut éditer, user non
        }
        
        return metadata.paramDefinitions[paramKey][permission] ?? (permission === 'editable_by_dev' ? true : false);
    }
    /**
     * Mappe le type de paramètre
     */
    private mapParamType(type: string): 'string' | 'number' | 'boolean' | 'video' | 'image' | 'object' {
        if (type === 'number') return 'number';
        if (type === 'boolean') return 'boolean';
        return 'string';
    }

    /**
     * Infère le type d'une valeur
     */
    private inferType(value: any): 'string' | 'number' | 'boolean' | 'video' | 'image' | 'object' {
        if (typeof value === 'number') return 'number';
        if (typeof value === 'boolean') return 'boolean';
        if (typeof value === 'object') return 'object';
        if (typeof value === 'string') {
            if (value.endsWith('.mp4') || value.endsWith('.webm')) return 'video';
            if (value.endsWith('.jpg') || value.endsWith('.png') || value.endsWith('.webp')) return 'image';
        }
        return 'string';
    }

    /**
     * Formate un nom de clé en display name
     */
    private formatDisplayName(key: string): string {
        // Convertir camelCase en Title Case
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    }
}


